import {
  CircularProgress,
  FormControl,
  makeStyles,
  TextField,
  Typography,
  Checkbox,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React from "react";
import { useEffect, useState } from "react";

const useStyles = makeStyles(() => ({
  loader: {
    "& .MuiCircularProgress-root": {
      position: "absolute",
      right: "12px",
      top: "13px",
    },
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
}));

export function FetchOnceAutocompleteMultiple<T extends Named>({
  id,
  label,
  fetchOptions,
  onChange,
  error,
  helperText,
  value,
  renderOptionCustom,
  getOptionLabelCustom,
}: FetchOnceAutocompleteMultipleProps<T>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [fetched, setFetched] = useState(false);
  const loading = open && options.length === 0;

  const classes = useStyles();

  useEffect(() => {
    if (!loading) {
      return undefined;
    }

    (async () => {
      if (fetched) {
        return;
      }

      const fetchedOptions = await fetchOptions();
      fetchedOptions
        .filter((i) => !i.name)
        .forEach((i) => {
          i.name = "";
        });
      setFetched(true);
      setOptions(fetchedOptions);
    })();
  }, [loading, fetched]);

  let IconComponent = () => {
    return (
      <div style={{ position: "relative", right: "18px" }}>
        <svg
          width="8"
          height="6"
          viewBox="0 0 8 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.710051 2.21L3.30005 4.8C3.69005 5.19 4.32005 5.19 4.71005 4.8L7.30005 2.21C7.93005 1.58 7.48005 0.5 6.59005 0.5H1.41005C0.520051 0.5 0.0800515 1.58 0.710051 2.21Z"
            fill="#54606F"
          />
        </svg>
      </div>
    );
  };

  // Проверяем, все ли элементы выбраны
  const isAllSelected = value?.length > 0 && value?.length === options.length;

  // Обработчик "Выбрать все"
  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(options);
    }
  };

  // Сортируем опции: выбранные первыми, затем остальные
  const sortedOptions = React.useMemo(() => {
    if (!value || value.length === 0) {
      return options;
    }

    const selected: T[] = [];
    const unselected: T[] = [];

    options.forEach((option) => {
      const isSelected = value.some((v: any) =>
        v.id ? v.id === (option as any).id : v.name === option.name
      );

      if (isSelected) {
        selected.push(option);
      } else {
        unselected.push(option);
      }
    });

    return [...selected, ...unselected];
  }, [options, value]);

  return (
    <FormControl
      className="AutoCompleteInput"
      style={{ display: "flex", flexDirection: "column", width: "100%" }}
    >
      <label htmlFor={id}>
        <Typography>{label}</Typography>
      </label>
      <Autocomplete
        className={classes.loader}
        popupIcon={<IconComponent />}
        multiple
        value={value || []}
        fullWidth
        id={id}
        open={open}
        disableClearable
        disableCloseOnSelect
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onChange={(_event, newValue) => {
          onChange(newValue as T[]);
        }}
        getOptionLabel={(option) =>
          getOptionLabelCustom ? getOptionLabelCustom(option) : (option.name === undefined ? "" : option.name)
        }
        getOptionSelected={(option, value) => {
          // Сравниваем по id если есть, иначе по name
          if ((option as any).id && (value as any).id) {
            return (option as any).id === (value as any).id;
          }
          return option.name === value.name;
        }}
        options={sortedOptions}
        loading={loading}
        ListboxComponent={(props) => {
          const { children, ...other } = props;

          return (
            <ul {...other}>
              {/* Добавляем опцию "Выбрать все" */}
              <li
                className={classes.option}
                style={{
                  padding: "6px 16px",
                  cursor: "pointer",
                  borderBottom: "1px solid #e0e0e0",
                }}
                onClick={handleSelectAll}
              >
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={value?.length > 0 && !isAllSelected}
                  style={{ padding: "0" }}
                  color="primary"
                />
                <span style={{ fontWeight: 500 }}>Выбрать все</span>
              </li>
              {children}
            </ul>
          );
        }}
        renderOption={(option, { selected }) => (
          <div className={classes.option}>
            <Checkbox
              checked={selected}
              style={{ padding: "0" }}
              color="primary"
            />
            {renderOptionCustom ? renderOptionCustom(option) : <span>{option.name}</span>}
          </div>
        )}
        renderInput={(params) => {
          // Когда список закрыт и есть выбранные элементы - показываем "Выбрано: N"
          const displayValue = !open && value?.length > 0
            ? `Выбрано: ${value.length}`
            : "";

          return (
            <TextField
              {...params}
              variant="outlined"
              error={error}
              helperText={helperText}
              InputProps={{
                ...params.InputProps,
                startAdornment: null, // Убираем стандартные chips
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                // Когда закрыт - показываем количество, когда открыт - обычный input для поиска
                ...((!open && value?.length > 0) ? { value: displayValue } : {}),
              }}
            />
          );
        }}
      />
    </FormControl>
  );
}

interface FetchOnceAutocompleteMultipleProps<T extends Named> {
  id: string;
  label: string;
  fetchOptions: () => Promise<Array<T>>;
  onChange: (el: T[]) => void;
  error?: boolean;
  helperText?: string;
  value: T[];
  renderOptionCustom?: (option: T) => React.ReactNode;
  getOptionLabelCustom?: (option: T) => string;
}

interface Named {
  name: string;
}
